import { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Shield,
  Users,
  UserPlus,
  Link2,
  Copy,
  Check,
  Trash2,
  ClipboardList,
  Activity,
  Clock,
  FileText,
  User,
  Settings,
  AlertTriangle,
  Mail
} from "lucide-react";
import { toast } from "sonner";

const AdminPage = () => {
  const { user, api } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState([]);
  const [invites, setInvites] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [newInviteRole, setNewInviteRole] = useState("team_member");
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [copiedInvite, setCopiedInvite] = useState(null);
  const [roleSelectOpen, setRoleSelectOpen] = useState(false);

  // Check if user is admin
  useEffect(() => {
    if (user && user.role !== "admin") {
      toast.error("Admin access required");
      navigate("/dashboard");
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [teamRes, invitesRes, logsRes] = await Promise.all([
        api.get("/team"),
        api.get("/invites"),
        api.get("/activity-logs?limit=50")
      ]);
      setTeam(teamRes.data);
      setInvites(invitesRes.data);
      setActivityLogs(logsRes.data);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvite = async () => {
    setCreatingInvite(true);
    try {
      const response = await api.post("/invites", { role: newInviteRole });
      const inviteLink = `${window.location.origin}/invite/${response.data.invite_code}`;
      
      // Refresh invites list
      const invitesRes = await api.get("/invites");
      setInvites(invitesRes.data);
      
      // Copy to clipboard
      await navigator.clipboard.writeText(inviteLink);
      toast.success("Invite link created and copied to clipboard!");
      setInviteDialogOpen(false);
    } catch (error) {
      console.error("Error creating invite:", error);
      toast.error("Failed to create invite");
    } finally {
      setCreatingInvite(false);
    }
  };

  const handleCopyInvite = async (inviteCode) => {
    const inviteLink = `${window.location.origin}/invite/${inviteCode}`;
    await navigator.clipboard.writeText(inviteLink);
    setCopiedInvite(inviteCode);
    toast.success("Invite link copied!");
    setTimeout(() => setCopiedInvite(null), 2000);
  };

  const handleCopyInviteMessage = async (invite) => {
    const inviteLink = `${window.location.origin}/invite/${invite.invite_code}`;
    const roleText = invite.role === "admin" ? "an Admin" : "a Team Member";
    
    const message = `Hi!

${user?.name || 'Eli'} has invited you to join Refund Scope as ${roleText}.

Click the link below to accept your invitation:
${inviteLink}

This invite link will expire in 7 days.`;
    
    await navigator.clipboard.writeText(message);
    toast.success("Invite message copied to clipboard!");
  };

  const handleDeleteInvite = async (inviteId) => {
    try {
      await api.delete(`/invites/${inviteId}`);
      setInvites(invites.filter(i => i.invite_id !== inviteId));
      toast.success("Invite deleted");
    } catch (error) {
      console.error("Error deleting invite:", error);
      toast.error("Failed to delete invite");
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/team/${userId}/role`, { role: newRole });
      setTeam(team.map(member => 
        member.user_id === userId ? { ...member, role: newRole } : member
      ));
      toast.success("Role updated successfully");
      // Refresh activity logs
      const logsRes = await api.get("/activity-logs?limit=50");
      setActivityLogs(logsRes.data);
    } catch (error) {
      console.error("Error updating role:", error);
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm("Are you sure you want to remove this team member?")) return;
    
    try {
      await api.delete(`/team/${userId}`);
      setTeam(team.filter(m => m.user_id !== userId));
      toast.success("Team member removed");
      // Refresh activity logs
      const logsRes = await api.get("/activity-logs?limit=50");
      setActivityLogs(logsRes.data);
    } catch (error) {
      console.error("Error removing user:", error);
      toast.error(error.response?.data?.detail || "Failed to remove user");
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "create_client": return <User className="w-4 h-4 text-emerald-500" />;
      case "delete_client": return <Trash2 className="w-4 h-4 text-rose-500" />;
      case "upload_document": return <FileText className="w-4 h-4 text-blue-500" />;
      case "run_analysis": return <Activity className="w-4 h-4 text-purple-500" />;
      case "update_role": return <Shield className="w-4 h-4 text-amber-500" />;
      case "remove_team_member": return <Users className="w-4 h-4 text-rose-500" />;
      case "create_invite": return <UserPlus className="w-4 h-4 text-emerald-500" />;
      default: return <ClipboardList className="w-4 h-4 text-slate-500" />;
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const formatExpiry = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    if (date < now) return "Expired";
    const diffDays = Math.ceil((date - now) / 86400000);
    return `${diffDays} days`;
  };

  if (user?.role !== "admin") {
    return null;
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight flex items-center gap-3">
              <Shield className="w-8 h-8 text-amber-500" />
              Admin Dashboard
            </h1>
            <p className="text-slate-500 font-body mt-1">
              Manage team access, invitations, and view activity logs
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{team.length}</p>
                  <p className="text-sm text-slate-500">Team Members</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-emerald-100 rounded-lg">
                  <Link2 className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{invites.filter(i => !i.used).length}</p>
                  <p className="text-sm text-slate-500">Active Invites</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Activity className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{activityLogs.length}</p>
                  <p className="text-sm text-slate-500">Recent Activities</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="team" className="space-y-4">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="team" className="font-body">
              <Users className="w-4 h-4 mr-2" />
              Team Members
            </TabsTrigger>
            <TabsTrigger value="invites" className="font-body">
              <Link2 className="w-4 h-4 mr-2" />
              Invitations
            </TabsTrigger>
            <TabsTrigger value="activity" className="font-body">
              <ClipboardList className="w-4 h-4 mr-2" />
              Activity Logs
            </TabsTrigger>
          </TabsList>

          {/* Team Members Tab */}
          <TabsContent value="team">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Team Members</CardTitle>
                <CardDescription>Manage your team's access and roles</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {team.map((member) => (
                      <TableRow key={member.user_id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="w-8 h-8">
                              <AvatarImage src={member.picture} alt={member.name} />
                              <AvatarFallback className="bg-slate-100 text-slate-600">
                                {member.name?.charAt(0)?.toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-medium">{member.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-500">{member.email}</TableCell>
                        <TableCell>
                          <Select
                            value={member.role}
                            onValueChange={(value) => handleRoleChange(member.user_id, value)}
                            disabled={member.user_id === user.user_id}
                          >
                            <SelectTrigger className="w-[140px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="team_member">Team Member</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right">
                          {member.user_id !== user.user_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                              onClick={() => handleRemoveUser(member.user_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invitations Tab */}
          <TabsContent value="invites">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading">Team Invitations</CardTitle>
                  <CardDescription>Create and manage invite links for new team members</CardDescription>
                </div>
                <Button onClick={() => setInviteDialogOpen(true)} className="bg-slate-900 hover:bg-slate-800">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create Invite
                </Button>
              </CardHeader>
              <CardContent>
                {invites.length === 0 ? (
                  <div className="text-center py-8">
                    <Link2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No invitations yet</p>
                    <p className="text-sm text-slate-400">Create an invite to add team members</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Invite Code</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Created By</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Expires</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invites.map((invite) => (
                        <TableRow key={invite.invite_id}>
                          <TableCell className="font-mono text-sm">{invite.invite_code}</TableCell>
                          <TableCell>
                            <Badge variant={invite.role === "admin" ? "default" : "secondary"}>
                              {invite.role === "admin" ? "Admin" : "Team Member"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-500">{invite.created_by_name}</TableCell>
                          <TableCell>
                            {invite.used ? (
                              <Badge variant="outline" className="bg-slate-100">Used</Badge>
                            ) : (
                              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Active</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-slate-500">{formatExpiry(invite.expires_at)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {!invite.used && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleCopyInviteMessage(invite)}
                                    title="Copy invite message"
                                    className="text-xs px-2"
                                  >
                                    <Mail className="w-4 h-4 mr-1" />
                                    Copy Message
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleCopyInvite(invite.invite_code)}
                                    title="Copy link only"
                                  >
                                    {copiedInvite === invite.invite_code ? (
                                      <Check className="w-4 h-4 text-emerald-500" />
                                    ) : (
                                      <Copy className="w-4 h-4" />
                                    )}
                                  </Button>
                                </>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                onClick={() => handleDeleteInvite(invite.invite_id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Logs Tab */}
          <TabsContent value="activity">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Activity Logs</CardTitle>
                <CardDescription>Track all team actions and changes</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {activityLogs.length === 0 ? (
                      <div className="text-center py-8">
                        <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">No activity logs yet</p>
                      </div>
                    ) : (
                      activityLogs.map((log) => (
                        <div 
                          key={log.log_id}
                          className="flex items-start gap-3 p-3 rounded-lg border border-slate-100 hover:bg-slate-50"
                        >
                          <div className="mt-0.5">{getActionIcon(log.action)}</div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-900">
                              <span className="font-medium">{log.user_name}</span>
                              <span className="text-slate-500"> • {log.details}</span>
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Clock className="w-3 h-3 text-slate-400" />
                              <span className="text-xs text-slate-400">{formatTime(log.created_at)}</span>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create Invite Dialog */}
        <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
          <DialogContent
            onPointerDownOutside={(e) => roleSelectOpen && e.preventDefault()}
            onEscapeKeyDown={(e) => roleSelectOpen && e.preventDefault()}
          >
            <DialogHeader>
              <DialogTitle className="font-heading">Create Team Invite</DialogTitle>
              <DialogDescription>
                Generate an invite link to add a new team member. The link will expire in 7 days.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Role for new member</Label>
                <Select 
                  open={roleSelectOpen}
                  onOpenChange={setRoleSelectOpen}
                  value={newInviteRole} 
                  onValueChange={setNewInviteRole}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="team_member">Team Member</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {newInviteRole === "admin" 
                    ? "Admins can manage team members, create invites, and delete clients."
                    : "Team members can view and manage clients but cannot delete or manage team."
                  }
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateInvite} 
                disabled={creatingInvite}
                className="bg-slate-900 hover:bg-slate-800"
              >
                {creatingInvite ? "Creating..." : "Create & Copy Link"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdminPage;
